import { Hono } from "hono";
import { z } from "zod";

import { canManage } from "../../contracts/values";
import type { AuthorizeModuleMutation, ModuleMutationActor, ModuleRouteEnvironment } from "../contract";
import {
  BELABOX_MODULE_ID,
  BELABOX_POLL_ALARM_KEY,
  BELABOX_STATS_URL_SECRET,
  type BelaboxTestResult,
} from "./contracts";
import {
  getBelaboxStatus,
  getBelaboxLiveHistory,
  getBelaboxStreamHistory,
  listBelaboxStreams,
} from "./adapters/d1";
import { validateBelaboxStatsUrl } from "./domain/stats-url";
import { belaboxSettingsForChannel, type BelaboxPollRoutineResult } from "./service";

const testSchema = z.object({ url: z.string().optional() });

const channelIdOf = (context: { req: { param: (name: string) => string | undefined } }): string =>
  context.req.param("channelId") ?? "";

const managementDenied = (context: { json: (body: { error: string }, status: 403) => Response }): Response =>
  context.json({ error: "belabox_management_denied" }, 403);

const hasMemberAuthorization = async (
  db: D1Database,
  channelId: string,
  actor: ModuleMutationActor,
  authorizeMutation: AuthorizeModuleMutation,
): Promise<boolean> => {
  const authorization = authorizeMutation(channelId, actor, new Date().toISOString());
  const row = await db.prepare(`SELECT 1 AS authorized WHERE 1 = 1 ${authorization.sql}`)
    .bind(...authorization.values).first<{ authorized: number }>();
  return row !== null;
};

export const resetSampleAfterStatsUrlChangeBestEffort = async (
  runModuleAlarm: ModuleRouteEnvironment["Variables"]["runModuleAlarm"],
  writeModuleDiagnostics: ModuleRouteEnvironment["Variables"]["writeModuleDiagnostics"],
  db: D1Database,
  channelId: string,
): Promise<void> => {
  try {
    await runModuleAlarm(channelId, BELABOX_MODULE_ID, BELABOX_POLL_ALARM_KEY, BELABOX_POLL_ALARM_KEY, {
      reason: "configuration_changed",
    });
  } catch {
    try {
      await writeModuleDiagnostics(
        db,
        channelId,
        BELABOX_MODULE_ID,
        "belabox:poll:configuration_changed",
        null,
        [{ code: "belabox.polling_ensure_failed" }],
        new Date().toISOString(),
      );
    } catch {
      // Diagnostics never change a successful configuration response.
    }
  }
};

const checkNowBestEffort = async (
  runModuleAlarm: ModuleRouteEnvironment["Variables"]["runModuleAlarm"],
  writeModuleDiagnostics: ModuleRouteEnvironment["Variables"]["writeModuleDiagnostics"],
  db: D1Database,
  channelId: string,
): Promise<void> => {
  try {
    await runModuleAlarm(channelId, BELABOX_MODULE_ID, BELABOX_POLL_ALARM_KEY, BELABOX_POLL_ALARM_KEY, {
      reason: "check_now",
    });
  } catch {
    try {
      await writeModuleDiagnostics(
        db,
        channelId,
        BELABOX_MODULE_ID,
        "belabox:poll:check_now",
        null,
        [{ code: "belabox.polling_ensure_failed" }],
        new Date().toISOString(),
      );
    } catch {
      // Diagnostics never change a successful control response.
    }
  }
};

export const belaboxRoutes = new Hono<ModuleRouteEnvironment>();

belaboxRoutes.get("/status", async (context) => {
  const channelId = channelIdOf(context);
  const secrets = context.get("secrets")(channelId);
  const [secretStatus, moduleState, streamState] = await Promise.all([
    secrets.status(BELABOX_STATS_URL_SECRET),
    belaboxSettingsForChannel(context.env.DB, channelId),
    context.env.DB.prepare(
      "SELECT state, stream_id FROM channel_stream_state WHERE channel_id = ?",
    ).bind(channelId).first<{ state: string; stream_id: string | null }>(),
  ]);
  const status = await getBelaboxStatus(context.env.DB, channelId);
  const pollingDesired = moduleState?.enabled === true && moduleState.settings.mode === "interval" &&
    secretStatus.configured && streamState?.state === "online";
  const classifiedBelaboxStream = moduleState?.enabled === true && moduleState.settings.mode === "interval" &&
    moduleState.settings.alertsEnabled && streamState?.state === "online" && streamState.stream_id !== null &&
    status?.streamId === streamState.stream_id && status.belaboxStreamId === streamState.stream_id;
  const alertPhase = classifiedBelaboxStream && status.alertState.phase !== "ok" && status.alertState.kind !== null
    ? { phase: status.alertState.phase, kind: status.alertState.kind, bitrateKbps: status.sample?.bitrateKbps ?? null }
    : null;
  return context.json({
    ...secretStatus,
    mode: moduleState?.settings.mode ?? null,
    sample: status?.sample ?? null,
    errorCode: status?.errorCode ?? null,
    polling: status?.polling ?? false,
    pollingDesired,
    streamId: status?.streamId ?? null,
    belaboxStreamId: status?.belaboxStreamId ?? null,
    alertNotice: alertPhase,
    fetchFailureNotice: classifiedBelaboxStream && status.fetchPhase.failing,
    intervalSeconds: moduleState?.settings.intervalSeconds ?? 15,
  });
});

belaboxRoutes.get("/history", async (context) => {
  const channelId = channelIdOf(context);
  const range = context.req.query("range");
  if (range !== "live" && range !== "stream") return context.json({ error: "belabox_history_range_invalid" }, 400);
  const moduleState = await belaboxSettingsForChannel(context.env.DB, channelId);
  if (moduleState?.enabled !== true || moduleState.settings.mode !== "interval") return context.json([]);
  if (range === "live") return context.json(await getBelaboxLiveHistory(context.env.DB, channelId));

  const requestedStreamId = context.req.query("streamId");
  if (requestedStreamId !== undefined && (requestedStreamId.length === 0 || requestedStreamId.length > 128)) {
    return context.json({ error: "belabox_history_stream_invalid" }, 400);
  }
  const status = requestedStreamId === undefined ? await getBelaboxStatus(context.env.DB, channelId) : null;
  const streamId = requestedStreamId ?? status?.belaboxStreamId;
  if (streamId === null || streamId === undefined) return context.json([]);
  return context.json(await getBelaboxStreamHistory(context.env.DB, channelId, streamId));
});

belaboxRoutes.get("/streams", async (context) => {
  const channelId = channelIdOf(context);
  return context.json(await listBelaboxStreams(context.env.DB, channelId));
});

belaboxRoutes.post("/polling/retry", async (context) => {
  if (!canManage(context.get("channelRole"))) return managementDenied(context);
  await checkNowBestEffort(
    context.get("runModuleAlarm"),
    context.get("writeModuleDiagnostics"),
    context.env.DB,
    channelIdOf(context),
  );
  return context.json({ ensured: true });
});

belaboxRoutes.post("/test", async (context) => {
  const channelId = channelIdOf(context);
  const actor = context.get("actor");
  const authorizeMutation = context.get("authorizeMutation");
  if (!await hasMemberAuthorization(context.env.DB, channelId, actor, authorizeMutation)) {
    return managementDenied(context);
  }
  const parsed = testSchema.safeParse(await context.req.json().catch(() => ({})));
  if (!parsed.success) return context.json({ error: "belabox_test_invalid" }, 400);

  if (!await hasMemberAuthorization(context.env.DB, channelId, actor, authorizeMutation)) {
    return managementDenied(context);
  }

  const usesStoredUrl = parsed.data.url === undefined;
  const storedSecret = usesStoredUrl
    ? await context.get("secrets")(channelId).readWithVersion(BELABOX_STATS_URL_SECRET)
    : null;
  const input = usesStoredUrl ? storedSecret?.value : parsed.data.url;
  if (input === undefined) {
    return context.json({ ok: false, reason: "not_configured" } satisfies BelaboxTestResult);
  }
  const validation = validateBelaboxStatsUrl(input);
  if (!validation.ok) {
    return context.json({ ok: false, reason: validation.reason } satisfies BelaboxTestResult);
  }

  if (!await hasMemberAuthorization(context.env.DB, channelId, actor, authorizeMutation)) {
    return managementDenied(context);
  }

  let result: unknown;
  try {
    result = await context.get("runModuleAlarm")(
      channelId,
      BELABOX_MODULE_ID,
      BELABOX_POLL_ALARM_KEY,
      BELABOX_POLL_ALARM_KEY,
      { reason: "connection_test", statsUrl: input },
    );
  } catch {
    return context.json({ ok: false, reason: "network" } satisfies BelaboxTestResult);
  }
  if (typeof result !== "object" || result === null || !("ok" in result)) {
    return context.json({ ok: false, reason: "network" } satisfies BelaboxTestResult);
  }
  const pollResult = result as BelaboxPollRoutineResult;
  if (!pollResult.ok) {
    const reason = pollResult.reason === "stream_changed" ? "network" : pollResult.reason;
    return context.json({ ok: false, reason } satisfies BelaboxTestResult);
  }

  return context.json({
    ok: true,
    connected: pollResult.sample.connected,
    bitrateKbps: pollResult.sample.bitrateKbps,
  } satisfies BelaboxTestResult);
});
